import { identity } from 'lodash-es';
import { useState } from 'react';

import type {
  BaseOptions,
  CompatibleStorageValue,
  TransformedValueOnWrite,
  TransformValueOnRead,
} from './types';
import { useInitStorageStateValue } from './useInitStorageStateValue';
import { useSynchronizeStorage } from './useSynchronizeStorage';
import { DEFAULT_VALUE_DURATION, defaultParse, defaultStringify } from './utils';

export interface UseLocalStorageStateOptions<
  T extends CompatibleStorageValue,
  PersistedParsedValue extends CompatibleStorageValue = T,
> extends Partial<BaseOptions<T, PersistedParsedValue>> {
  when?: boolean;
  shouldSuspendUpdateOnPageInvisibility?: boolean;
}

export const getTypedDefaultOptions = <
  T extends CompatibleStorageValue,
  PersistedParsedValue extends CompatibleStorageValue = T,
>() => ({
  when: true,
  migrationStrategy: 'none' as const,
  shouldSuspendUpdateOnPageInvisibility: false,
  version: 2 as const,
  duration: DEFAULT_VALUE_DURATION,
  stringify: defaultStringify,
  parse: defaultParse,
  transformValueOnRead: identity as TransformValueOnRead<T, PersistedParsedValue>,
  transformValueOnWrite: identity as TransformedValueOnWrite<T, PersistedParsedValue>,
});

const useLocalStorageState = <
  T extends CompatibleStorageValue,
  PersistedParsedValue extends CompatibleStorageValue = T,
>(
  unversionedKey: string,
  emptyValue: T,
  options: UseLocalStorageStateOptions<T, PersistedParsedValue> = {},
) => {
  const optionsWithDefaults = {
    ...getTypedDefaultOptions<T, PersistedParsedValue>(),
    ...options,
  };
  const {
    when,
    migrationStrategy,
    shouldSuspendUpdateOnPageInvisibility,
    version,
    duration,
    stringify,
    parse,
    transformValueOnRead,
    transformValueOnWrite,
  } = optionsWithDefaults;
  const { getInitState, whenRef } = useInitStorageStateValue<T, PersistedParsedValue>({
    unversionedKey,
    migrationStrategy,
    transformValueOnRead,
    version,
    duration,
    stringify,
    parse,
    emptyValue,
    when,
  });

  const [storedValue, setStoredValue] = useState<T>(getInitState);

  useSynchronizeStorage({
    storedValue,
    setStoredValue,
    shouldSuspendUpdateOnPageInvisibility,
    whenRef,
    unversionedKey,
    version,
    duration,
    stringify,
    parse,
    transformValueOnRead,
    transformValueOnWrite,
    emptyValue,
  });

  return [storedValue, setStoredValue] as const;
};

export { useLocalStorageState };

export { useInitStorageStateValue, useSynchronizeStorage };

export {
  defaultParse as defaultLocalStorageParse,
  defaultStringify as defaultLocalStorageStringify,
  getTypedDefaultOptions as getLocalStorageStateDefaultOptions,
};

export type { CompatibleStorageValue } from './types';
